import { useState } from 'react';
import { startRegistration } from '@simplewebauthn/browser';
import type { KeyedMutator } from 'swr';
import type { IMfaPasskeyOptionsView, IMfaStatusView } from '@shared/services';
import { addPasskeyOptions, addPasskeyVerify, removePasskey as removePasskeyRequest } from '@/shared/api/mfa';
import { getApiErrorString, isRetryablePasskeyError, passkeyErrorMessage } from '@/shared/utils';

export interface AddPasskeyError {
  message: string;
  /** Whether offering a "try again" makes sense (false for unsupported device / insecure origin). */
  retryable: boolean;
}

/**
 * Add/remove passkeys against the session-authorized management API, revalidating the shared
 * `useMfaStatus` cache via `mutate` on success so the list updates without a full reload. The add
 * flow mirrors the login-time ceremony (`options` → `startRegistration` → `verify`) but adopts no
 * session — the user is already signed in.
 */
export const usePasskeyManagement = (mutate: KeyedMutator<IMfaStatusView>) => {
  const [isAdding, setIsAdding] = useState(false);
  const [addError, setAddError] = useState<AddPasskeyError | null>(null);

  // The stringified WebAuthn response held between the ceremony and naming steps: non-null means the
  // credential was created in the authenticator and is awaiting a name before we verify it server-side.
  const [pendingResponse, setPendingResponse] = useState<string | null>(null);
  const [isSavingPasskey, setIsSavingPasskey] = useState(false);

  const [removingId, setRemovingId] = useState<string | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);

  // Step 0 (step-up): fetch the WebAuthn creation options, which the gateway now gates behind the
  // current password (`MfaStepUpDto`). Kept as the `useStepUp` start call so a rejected password
  // surfaces inline in the prompt and blocks the ceremony; throws on rejection rather than swallowing.
  const requestPasskeyOptions = (currentPassword: string): Promise<IMfaPasskeyOptionsView> =>
    addPasskeyOptions({ currentPassword });

  // Step 1: run the WebAuthn registration ceremony from the already-fetched options (password already
  // accepted). On success the credential exists in the authenticator but is NOT yet registered
  // server-side — we hold the response and move to naming.
  const runCeremony = async (optionsJSON: string) => {
    setAddError(null);
    setIsAdding(true);

    try {
      const response = await startRegistration({ optionsJSON: JSON.parse(optionsJSON) });
      setPendingResponse(JSON.stringify(response));
    } catch (caught) {
      setAddError({ message: passkeyErrorMessage(caught), retryable: isRetryablePasskeyError(caught) });
    } finally {
      setIsAdding(false);
    }
  };

  // Step 2: name the passkey and verify it server-side. Returns whether it succeeded so the caller can
  // keep the naming step open on failure (the response is still valid to retry within its window).
  const confirmAddPasskey = async (label: string): Promise<boolean> => {
    if (!pendingResponse) {
      return false;
    }

    setAddError(null);
    setIsSavingPasskey(true);

    try {
      const trimmed = label.trim();
      await addPasskeyVerify({ response: pendingResponse, ...(trimmed ? { label: trimmed } : {}) });
      await mutate();
      setPendingResponse(null);

      return true;
    } catch (caught) {
      setAddError({ message: passkeyErrorMessage(caught), retryable: isRetryablePasskeyError(caught) });

      return false;
    } finally {
      setIsSavingPasskey(false);
    }
  };

  // Abandon the naming step. The unverified credential is simply never registered server-side.
  const cancelAddPasskey = () => {
    setPendingResponse(null);
    setAddError(null);
  };

  /** Returns whether the removal succeeded, so the caller can keep the confirm dialog open on failure. */
  const removePasskey = async (credentialId: string): Promise<boolean> => {
    setRemoveError(null);
    setRemovingId(credentialId);

    try {
      await removePasskeyRequest({ credentialId });
      await mutate();

      return true;
    } catch (caught) {
      // Keep the item in the list on failure — nothing was removed server-side.
      setRemoveError(getApiErrorString(caught));

      return false;
    } finally {
      setRemovingId(null);
    }
  };

  return {
    requestPasskeyOptions,
    runCeremony,
    isAdding,
    confirmAddPasskey,
    cancelAddPasskey,
    isNamingPasskey: pendingResponse !== null,
    isSavingPasskey,
    addError,
    dismissAddError: () => setAddError(null),
    removePasskey,
    removingId,
    removeError,
    dismissRemoveError: () => setRemoveError(null),
  };
};
